<template>
    <div class="container devel">
        <h2 class="pt-5">正式リリース版について</h2>
        <div class="text-muted small">
            貸し本棚は2022年12月に正式リリース版となりました。
            リリース版以後、開発方針を変更することはありません。
        </div>
        <div class="row">
            <img src="/img/static/production.jpg" class="mt-5 img-fluid" alt="外から内へより深く">
        </div>
        <h3 class="caption">貸し本棚の開発方針</h3>
        <div class="desc">
            <p>
                貸し本棚は、作家様、読者様の双方にとって、
                執筆すること、読書することに没頭できるような、内向きの機能を充実させていきます。
            </p>
            <p>
                その一方で、ユーザー間の過度な交流を目的とした外向き機能開発に対してはあまり積極的ではありません。
                そういったソーシャルな機能は他のサービスが十二分に提供しているので、
                外部連携などで対応していく方針です。
                作家様と作品の愛読者との間で、誰にも邪魔されない小規模な交流ができるような機能に絞って開発を行います。
            </p>
        </div>
        <h3 class="caption">正式リリース版</h3>
        <div class="desc">
            <p>
                現在、貸し本棚は<b>正式リリース版</b>となっています。
            </p>
            <p>
                これまでのバージョンでは、破壊的変更が加えられたり、開発方針が変わる可能性がありました。
                正式リリース版では方向性について変更が行われることは原則としてありません。
            </p>
            <p>
                正式リリース版となっても、開発は継続されます。
                意欲的な新機能や既存機能の拡充、デザインの調整、チューニングなどを行っていきます。
                破壊的な変更、何らかの機能の廃止にあたっては、事前に告知されます。
            </p>
        </div>
        <h3 class="caption">貸し本棚のルーツ</h3>
        <div class="desc">
            <p>
                小説執筆サービスを開発しようと決めたときに、最初にサービス名を
                「貸し本棚」に決めました。設計も何もしてない段階です。
            </p>
            <p>
                その時点で被りがないか「貸し本棚」で検索したところ、ほとんど記事も広告もなく、
                カリグラシコム様のこちらの記事
                <a href="https://uchi-machi-danchi.ur-net.go.jp/ours/028/" target="_blank">まちで小さな本棚を借りてみたら</a>
                が最上位にヒットしました。
                こちらは本当の物理的な本棚を低価格で借りて、お勧めの書籍や古本を販売できる貸しスペースのようです。
                その中のこの一節
            </p>
            <blockquote class="blockquote b-light">
                本棚に実際に並べることで、本が人目に触れる（<b>かもしれない</b>）こと。
                本屋さんに行かないような人にも買ってもらえる（<b>かもしれない</b>）こと。
                SNSで「入荷しました！」と書くことで、その本をさらに宣伝することもできる。
                「借りる」が、わたしを本の宣伝隊長にしてくれた。
            </blockquote>
            <p>
                これが貸し本棚開発理念の発端となったアイデアです。
            </p>
            <div class="row">
                <img src="/img/static/owned.jpg" class="mb-3 img-fluid" alt="本棚を所有する">
            </div>
            <p>
                「本棚を所有する」という概念を実現するために、作家様のためにサブドメインを用意しました。
                サブディレクトリのような従属ではなく、検索エンジンに独立したサイトとして扱われるためです。
                このサブドメイン提供型のサービスは特段新しいものではないのですが、
                ポータルドメインのSEOが悪化するという奇妙な迷信があり、
                わざわざこんな機能を実装する酔狂なサービスはなかなかありません。
                そういう愚策をあえて実行できるのが個人開発です。
            </p>
        </div>
        <h4>１．執筆サービス</h4>
        <div>
            <p>
                貸し本棚のエディタ機能は、小説を記述するのに優れた機能を実装しています。
                見やすいテキストエディタに、大手投稿サイトの書式のルビ打ちをショートカットでき、
                梗概や設定、登場人物を記載したノートを並べて執筆作業を行えます。<br>
                熟練の作家さんにとって妨げにしかならない校正機能などはありません。
                書いたものが書いたまま登録されます。作者の入力情報を勝手に変更することは決してありません。
            </p>
            <p>
                長編小説の場合、エピソード間を横断的に移動し、一括検索・置換を実行でき、
                長過ぎる本文を分解したり、短いエピソードを結合することもできます。
            </p>
            <p>
                仕上がった作品を貸し本棚上で公開することで、
                最終的に読者からどのようにみえるかということを事前に確認することができます。
            </p>
        </div>
        <h4>２．作品の保管場所に</h4>
        <div>
            <p>
                貸し本棚はあらゆる作品の置き場所として最適です。
                原則として、R18作品も含め、適切にセルフレーティングされていれば、作品が運営によって取り下げられることはありません。
            </p>
            <p>
                また、過去作をテキストファイルから一括で取り込むことができます。
                小説家になろう形式であれば自動解析し、数百話でも瞬時に全登録することが可能です。
            </p>
            <p>
                登録した作品の最新版を、登録メールアドレスにzip圧縮して送信するバックアップ設定が可能です。
                これにより、サイトで重大な問題が発生したとしても、登録作品の情報が失われることはありません。
                「大量の作品を登録したけれど、引っ越しできなくなった！」ということは起こりません。
            </p>
            <p>
                また、貸し本棚には「記事」を投稿する機能があります。
                これはジャンル、シリーズ、小説自体に公開することができ、ブログのようなレイアウト調整機能や画像の投稿が可能です。
                この記事機能によって、他の投稿サービスにはない、作品の深堀りした設定、登場人物紹介、
                執筆の裏側、奥付、活動記録や創作論について幅広いテキストを公開することができます。
            </p>
        </div>
        <h4>３．ポートフォリオとして</h4>
        <div>
            <p>
                複数の投稿サイトに横断的にバージョンの異なる作品を投稿されている場合、
                それらを貸し本棚に統合して登録することで、SNSなどでご自身の作品群を紹介するときに、
                余計な情報が溢れる投稿サイトのマイページよりも見やすく直感的なポートフォリオとして利用することができます。
                Web小説の作家さんにとっては、一つの名刺のようなものです。
            </p>
            <p>
                「他のサービスのマイページとはどう違うの？」と思われるかもしれません。<br>
                貸し本棚で提供される作家様の本棚は、作家様専用のサイト構造になっています。
                他の作家様の作品や記事、お勧めなどが表示されることはありません。
                固有のサブドメインを設定できるので、小説検索サイト様へ登録を行うこともできます。
            </p>
            <p>
                もしご自身のポートフォリオとして大手サイトのマイページを示したとき、
                それを見て訪問してくれた読者様は程なく他の作家様の作品を読み始めるでしょう。
            </p>
            <p>
                貸し本棚のあなたの本棚にアクセスした読者様が、他の作家様の作品をそこでみつけることはできません。
                貸し本棚のポータルへ移動するには、ページ最下部のフッターリンクを踏む必要があります。
                それ以外にリンクはありません。
                設定次第でこのフッターリンクやユーザー登録を隠し、完全に独立サイトのように振る舞うこともできます。
            </p>
        </div>
        <h3 class="caption">ご要望について</h3>
        <div>
            <p>
                貸し本棚は個人開発です。
            </p>
            <p>
                ご意見ご要望があれば、お問い合わせか、XのDMなどでお送りいただけると幸いです。
                現時点では着手できなくても、次のフェーズの検討課題としてプールされます。
            </p>
            <h4>注意点</h4>
            <p>
                以下のようなご要望については、検討に乗りにくいです。
            </p>
            <blockquote class="blockquote b-light">
                サイドバー表示をボタンからスライド式にしてほしいです。
            </blockquote>
            <p>
                直接仕様変更を指定するご要望は、目的がよくわからないため、実装までたどり着かないことがほとんどです。
            </p>
            <blockquote class="blockquote b-light">
                スマホでサイドバーボタンをタップするのが難しいです。
            </blockquote>
            <p>
                このように、いまご自身が抱えている問題を教えて下さい。
                その問題について再現性があり、問題の本質が機能やデザインに起因するものであるとき、
                改修案が送出されます。
            </p>
        </div>
    </div>
</template>

<style scoped lang="scss">
.container {
    max-width: 960px;
}
.caption {
    margin: 4rem 0 .8rem;
    padding: .5rem 0;
    border-bottom: 1px dashed #ddd;
}
.devel {
    h4 {
        margin-top: 2rem;
    }
    p {
        font-size: 1.0rem;
    }
}
.checklist {
    margin: 1rem 0;
}
.blockquote {
    border-radius: .5rem;
    padding: 1rem;
}
</style>

<script>
export default {
    mounted() {
        this.useHead({
            title: "正式リリース版について | 貸し本棚",
            link: [ { rel: "canonical", href: location.origin + location.pathname, id: "canonical" } ]
        });
    }
}
</script>